import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowCounterClockwise, Check, Copy, EnvelopeOpen, Paperclip } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi, getErrorMessage } from "@/lib/api";
import { btnGhost, btnPrimary } from "../ui";
import { fmtDate } from "../assess/parts";
import { EmailStatusPill, copyRich, htmlToText } from "./parts";

const CAN_HANDLE = ["waiting_connection", "failed"];
const DESC = {
  drafted: "This draft is in the warranty@ Outlook Drafts folder.",
  sending: "Sending from warranty@…",
  sent: "Sent from warranty@.",
  handled: "Marked as sent by hand.",
};

function Field({ label, value, testId }) {
  const copy = () => navigator.clipboard.writeText(value).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Couldn't copy"));
  return (
    <div className="flex items-center gap-3 text-xs border-b border-neutral-900 py-1.5" data-testid={testId}>
      <span className="text-neutral-500 w-14 shrink-0">{label}</span><span className="text-neutral-200 flex-1 break-all">{value || "—"}</span>
      {value && <button onClick={copy} className="text-neutral-500 hover:text-white" aria-label={`Copy ${label}`} data-testid={`${testId}-copy`}><Copy size={14} /></button>}
    </div>
  );
}

export function EmailPreview({ emailId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminApi.get(`/admin/wi/outbox/${emailId}`).then(({ data }) => setD(data)).catch((e) => toast.error(getErrorMessage(e))); }, [emailId]);
  const copy = () => copyRich(d.html).then(() => toast.success("Email copied. Paste it into a new email from warranty@")).catch(() => toast.error("Couldn't copy"));
  const mailto = d && `mailto:${d.to.join(",")}?${new URLSearchParams({ cc: d.cc.join(","), subject: d.subject, body: htmlToText(d.html) }).toString().replace(/\+/g, "%20")}`;
  const mark = async (handled) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/wi/outbox/${emailId}/handled`, { handled }); setD({ ...d, ...data }); onChanged?.();
      toast.success(handled ? "Marked as sent. Outlook won't get a draft of it" : `Back in the queue for Outlook${d.parts_ordered?.status === "done" ? ". Monday's Parts Ordered date and status stay as they are" : ""}`);
    }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wi-email-preview">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">{d?.label || "Email"} {d && <EmailStatusPill status={d.status} testId="wi-email-preview-status" />}</DialogTitle>
          <DialogDescription className="text-neutral-500" data-testid="wi-email-preview-desc">{d && (DESC[d.status] ? `${DESC[d.status]}${d.sent_at ? ` ${fmtDate(d.sent_at)}` : ""}` : "Outlook isn't creating this draft yet. Copy it and send it from warranty@, then mark it as sent.")}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="h-40 animate-pulse" /> : (
          <div className="space-y-4">
            <div><Field label="To" value={d.to.join(", ")} testId="wi-email-to" /><Field label="CC" value={d.cc.join(", ")} testId="wi-email-cc" /><Field label="Subject" value={d.subject} testId="wi-email-subject" /></div>
            {d.photo_asset_ids?.length > 0 && <p className="text-xs text-amber-300/90 flex items-center gap-1.5" data-testid="wi-email-photos-note"><Paperclip size={14} />{d.status === "drafted" ? `${d.attachments?.length || 0} photo(s) attached in Outlook` : `${d.photo_asset_ids.length} photo(s) from Monday.com are attached automatically once Outlook is connected. For now, attach them by hand.`}</p>}
            <div className="bg-white text-black rounded-xl p-5 text-sm [&_p]:mb-3 [&_p:last-child]:mb-0" data-testid="wi-email-body" dangerouslySetInnerHTML={{ __html: d.html }} />
            <div className="flex flex-wrap gap-2">
              <button className={btnPrimary} onClick={copy} data-testid="wi-email-copy-btn"><Copy size={15} /> Copy email</button>
              <a href={mailto} className={btnGhost} data-testid="wi-email-mailto-btn"><EnvelopeOpen size={15} /> Open in my email app</a>
              {CAN_HANDLE.includes(d.status) && <button className={btnGhost} disabled={busy} onClick={() => mark(true)} data-testid="wi-email-mark-sent-btn"><Check size={15} /> Mark as sent by hand</button>}
              {d.status === "handled" && <button className={btnGhost} disabled={busy} onClick={() => mark(false)} data-testid="wi-email-unmark-btn"><ArrowCounterClockwise size={15} /> Undo · not sent</button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
